import { Button, Divider } from '@mui/material';
import { ITeamRepository } from 'domain/team/Team.repository';
import { KeyValue } from 'primary/Components/KeyValue/KeyValue';
import { ActionButtonsForContent } from 'primary/MessageList/contents/ActionButtonsForContent';
import {
  getNavigationLinkFromRessourceKey,
  parseRessourceFromNotificaton,
} from 'primary/MessageList/NotificationListItem';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { NotificationResourceTypeEnum } from '../../../domain/notification/NotificationResourceTypeEnum';
import { UnpyNotification } from '../../../domain/notification/UnpyNotification';
import {
  TranslationOption,
  TranslationType,
} from '../../../domain/translation/Translation.repository';
import { useContextDependency } from '../../hooks/useContextDependency';
import { useRetrieveFromDomain } from '../../hooks/useRetrieveFromDomain';
import { useTranslate } from '../../hooks/useTranslate';

interface NotificationInvitationTeamEventContentProps {
  notification: UnpyNotification;
  reFetchNotification: () => void;
  closePreview?: () => void;
}

export const getOnClickResponseInvitationTeamEventParticipation = (
  teamRepository: ITeamRepository,
  t: (key: TranslationType, option?: TranslationOption) => string,
  response: boolean,
  reFetchNotification: () => void,
  teamId: number,
  eventId: number,
  setLoading: (loading: boolean) => void,
  accept: boolean,
) => {
  return () => {
    setLoading(true);
    if (accept) {
      teamRepository.acceptTeamInvitationEvent(response, teamId, eventId).then(() => {
        reFetchNotification();
        setLoading(false);
      });
    } else {
      teamRepository.declineTeamInvitationEvent(eventId, teamId).then(() => {
        reFetchNotification();
        setLoading(false);
      });
    }
  };
};

export const NotificationInvitationTeamEventContent = ({
  notification,
  reFetchNotification,
  closePreview,
}: NotificationInvitationTeamEventContentProps) => {
  const t = useTranslate();
  const { eventParticipantRepository, teamRepository, eventRepository, authRepository } =
    useContextDependency();
  const ressourcesParsed = parseRessourceFromNotificaton(notification);
  const navigate = useNavigate();
  const [eventParticipant] = useRetrieveFromDomain(
    () =>
      eventParticipantRepository.getEventParticipantById(
        parseInt(
          notification.resources[NotificationResourceTypeEnum.EVENT_PARTICIPANT_ID],
        ),
      ),
    undefined,
  );

  const [event] = useRetrieveFromDomain(
    () =>
      eventRepository.getEventById(
        parseInt(notification.resources[NotificationResourceTypeEnum.EVENT_ID]),
      ),
    undefined,
  );
  const [loading, setLoading] = React.useState(false);

  if (!eventParticipant || !event) return null;
  const teamPlayer = eventParticipant.team.players.find(
    (value) => value.player?.idPlayer === authRepository.currentUser?.userProfil.idPlayer,
  );

  return (
    <div>
      <div className={'-keyValueContainerNotification'}>
        {ressourcesParsed
          .filter((r) => !r.isLink)
          .map((res) => (
            <KeyValue
              key={res.keyValue}
              accent
              keyValue={t(
                `notificationList.item.ressourceLabelByKey.${
                  res.keyValue as NotificationResourceTypeEnum
                }`,
              )}
              value={res.value}
            />
          ))}
      </div>
      <div className={'-linkContainerNotification'}>
        {ressourcesParsed
          .filter(
            (r) =>
              r.isLink &&
              r.keyValue !== NotificationResourceTypeEnum.EVENT_PARTICIPANT_ID,
          )
          .map((res) => (
            <Button
              key={res.keyValue}
              variant={'outlined'}
              className={'-linkNotification'}
              onClick={() => {
                const link = getNavigationLinkFromRessourceKey(
                  res.keyValue as NotificationResourceTypeEnum,
                  res.value,
                  notification.resources,
                  notification.type,
                );
                navigate(link);
                closePreview?.();
              }}
            >
              {t(
                `notificationList.item.ressourceLabelByKey.${
                  res.keyValue as NotificationResourceTypeEnum
                }`,
              )}
            </Button>
          ))}
      </div>
      <Divider className={'-topMarged'} />
      {teamPlayer?.status === 'DECLINED' && (
        <span>{t('notification.content.joinStructure.alreadyRefused')}</span>
      )}
      {teamPlayer?.status === 'ACCEPTED' && (
        <span>{t('notification.content.joinStructure.alreadyAccepted')}</span>
      )}
      {teamPlayer?.status === 'PENDING' && (
        <ActionButtonsForContent
          loading={loading}
          onClickCancel={getOnClickResponseInvitationTeamEventParticipation(
            teamRepository,
            t,
            false,
            reFetchNotification,
            eventParticipant.team.id,
            event.id,
            setLoading,
            false,
          )}
          onClickValid={getOnClickResponseInvitationTeamEventParticipation(
            teamRepository,
            t,
            true,
            reFetchNotification,
            eventParticipant.team.id,
            event.id,
            setLoading,
            true,
          )}
        />
      )}
    </div>
  );
};
